import {Shell} from "@client/components/Shell";
import * as dates from "@client/dates";
import {useDialog} from "@client/features/dialogs";
import * as forms from "@client/forms";
import {useSiteContext} from "@client/site/context";
import * as utils from "@client/utils";
import {Schema, models, server} from "@reactivated";
import {Decimal} from "decimal.js";
import React from "react";

function Form(props: {form: models["forms.Form"]}) {
    return (
        <div>
            {props.form.fields.map((field) => (
                <div key={field.type}>{field.type}</div>
            ))}
        </div>
    );
}

function NutritionModule(props: {
    module: Extract<
        server.backoffice.views.Backoffice["module"]["module"],
        {type: "nutrition"}
    >;
}) {
    const columns = ["name", "calories_pg", "backend_id"] as const;

    const {request} = useSiteContext();
    const url = new URL(request.url);
    const query = url.searchParams.get("query") ?? "";

    return (
        <>
            <form action="" method="GET">
                <Form form={props.module.form} />
                <input type="text" name="query" value={query} />
                <button type="submit">Submit</button>
            </form>
            <form action="" method="POST">
                <forms.CSRFToken />
                <h2>Results</h2>
                <table className="table-auto">
                    <thead>
                        <tr className="text-left *:pr-4">
                            <td></td>
                            {columns.map((column) => (
                                <th key={column}>{column}</th>
                            ))}
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {props.module.results.map((result, index) => (
                            <tr key={index}>
                                <td>
                                    <input
                                        type="checkbox"
                                        name="is_checked"
                                        value={result.is_checked}
                                    />
                                </td>
                                {columns.map((column) => (
                                    <td key={column}>{result.item[column]}</td>
                                ))}
                                <td>
                                    <button
                                        type="submit"
                                        name="convert"
                                        value={result.item.backend_id}
                                    >
                                        Convert
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </form>
        </>
    );
}

function VisionPrompt(props: {
    prompt: Extract<
        server.backoffice.views.Backoffice["module"]["module"],
        {type: "vision"}
    >["comparisons"][number]["prompt"];
}) {
    return (
        <div className="grid grid-cols-4">
            {props.prompt.own_entries.map((entry) => (
                <React.Fragment key={entry.uuid}>
                    <div className="truncate whitespace-nowrap">{entry.name}</div>
                    <div>{new Decimal(entry.quantity).toFixed(1)}</div>
                    <div>{entry.unit}</div>
                    <div>
                        {utils.formatDecimalAndUnit(entry.calories, "cal", false)}
                    </div>
                </React.Fragment>
            ))}
            <div className="col-span-3">Total</div>
            <div className="col-span-1">
                {utils.formatDecimalAndUnit(
                    props.prompt.own_entries.reduce(
                        (acc, entry) => acc.add(new Decimal(entry.calories ?? "0")),
                        new Decimal(0),
                    ),
                    "cal",
                    false,
                )}
            </div>
        </div>
    );
}

function VisionModule(props: {
    module: Extract<
        server.backoffice.views.Backoffice["module"]["module"],
        {type: "vision"}
    >;
}) {
    console.log(props);

    return (
        <div className="grid grid-cols-3 gap-x-4 gap-y-12">
            {props.module.comparisons.map((comparison) => (
                <React.Fragment key={comparison.prompt.uuid}>
                    {comparison.prompt.image != null && (
                        <img
                            className="max-h-[200px] rounded-md object-contain"
                            src={`https://imagedelivery.net/ELiLiOfYeVi79Put2yGLDg/${comparison.prompt.image.image_uid}/public`}
                            alt="Uploaded image"
                        />
                    )}
                    <VisionPrompt prompt={comparison.prompt} />
                    {comparison.new_version != null ? (
                        <VisionPrompt prompt={comparison.new_version} />
                    ) : (
                        <div></div>
                    )}
                </React.Fragment>
            ))}
        </div>
    );
}

function RPCRequestModule(props: {
    module: Extract<
        server.backoffice.views.Backoffice["module"]["module"],
        {type: "rpc_request"}
    >;
}) {
    const columns = ["id", "user", "path", "status", "type", "created_at"] as const;

    const {request} = useSiteContext();
    const url = new URL(request.url);
    const query = url.searchParams.get("query") ?? "";

    return (
        <>
            <form action="" method="GET">
                <Form form={props.module.form} />
                <input type="text" name="query" defaultValue={query} />
                <button type="submit">Submit</button>
            </form>
            <form action="" method="POST">
                <forms.CSRFToken />
                <h2>Results</h2>
                <table className="table-auto">
                    <thead>
                        <tr className="text-left *:px-4">
                            <td></td>
                            {columns.map((column) => (
                                <th key={column}>{column}</th>
                            ))}
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {props.module.results.map((result, index) => (
                            <tr key={index} className="*:px-4">
                                <td>
                                    <input
                                        type="checkbox"
                                        name="is_checked"
                                        value={result.is_checked}
                                    />
                                </td>
                                {columns.map((column) => (
                                    <td key={column}>
                                        {column == "id" ? (
                                            <a href={result.admin_path}>{result.id}</a>
                                        ) : column == "created_at" ? (
                                            dates.formatBusinessDateTime(result[column])
                                        ) : (
                                            result[column]
                                        )}
                                    </td>
                                ))}
                                <td>
                                    <button
                                        type="submit"
                                        name="convert"
                                        value={result.id}
                                    >
                                        Convert
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </form>
        </>
    );
}

function TaskModule(props: {
    module: Extract<
        server.backoffice.views.Backoffice["module"]["module"],
        {type: "dbtaskresult"}
    >;
}) {
    const columns = [
        "id",
        "status",
        "enqueued_at",
        "started_at",
        "finished_at",
        "return_value",
    ] as const;

    const {request} = useSiteContext();
    const url = new URL(request.url);
    const query = url.searchParams.get("query") ?? "";

    return (
        <>
            <form action="" method="GET">
                <Form form={props.module.form} />
                <input type="text" name="query" defaultValue={query} />
                <button type="submit">Submit</button>
            </form>
            <form action="" method="POST">
                <forms.CSRFToken />
                <h2>Results</h2>
                <table className="table-auto">
                    <thead>
                        <tr className="text-left *:px-4">
                            <td></td>
                            {columns.map((column) => (
                                <th key={column}>{column}</th>
                            ))}
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {props.module.results.map((result, index) => (
                            <tr key={index} className="*:px-4">
                                <td>
                                    <input
                                        type="checkbox"
                                        name="is_checked"
                                        value={result.is_checked}
                                    />
                                </td>
                                {columns.map((column) => (
                                    <td key={column}>
                                        {column == "id" ? (
                                            <a href={result.admin_path}>{result.id}</a>
                                        ) : column == "return_value" ? (
                                            <pre>
                                                {JSON.stringify(
                                                    result[column],
                                                    null,
                                                    2,
                                                )}
                                            </pre>
                                        ) : column == "enqueued_at" ? (
                                            dates.formatBusinessDateTime(result[column])
                                        ) : (
                                            result[column]
                                        )}
                                    </td>
                                ))}
                                <td>
                                    <button
                                        type="submit"
                                        name="convert"
                                        value={result.id}
                                    >
                                        Convert
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </form>
        </>
    );
}

type AnalysisResult = Schema["rpc_reprocess_queued_image_output"];

function AnalyzeEntryDialogContent(props: {
    entryId: number;
    entryUuid: string;
    promptUuid: string;
    imageUid: string;
    originalName: string;
    close: (result: boolean | null) => void;
}) {
    const [description, setDescription] = React.useState("");
    const [isLoading, setIsLoading] = React.useState(false);
    const [analysis, setAnalysis] = React.useState<AnalysisResult | null>(null);
    const [error, setError] = React.useState<string | null>(null);

    const handleAnalyze = async () => {
        setIsLoading(true);
        setError(null);

        const result = await server.journal.nutritional_ai.reprocess_queued_image({
            prompt_uuid: props.promptUuid,
            description,
        });

        setIsLoading(false);

        if (result.type === "success") {
            setAnalysis(result.data);
        } else {
            setError("Failed to analyze entry");
        }
    };

    const handleApprove = async () => {
        if (analysis == null) return;

        setIsLoading(true);
        setError(null);

        const result = await server.journal.day.replace_entry({
            target_entry_uuid: props.entryUuid,
            replacement: analysis,
        });

        setIsLoading(false);

        if (result.type === "success") {
            window.location.reload();
        } else {
            setError("Failed to replace entry");
        }
    };

    const totalCalories = analysis?.expanded_recipe.reduce(
        (acc, ing) => acc + Number(ing.calories_pg ?? 0),
        0,
    );

    return (
        <div className="max-w-2xl p-4">
            <h2 className="mb-4 text-lg font-semibold">Analyze Entry</h2>

            <div className="mb-4">
                <img
                    className="mx-auto max-h-[300px] rounded-md object-contain"
                    src={`https://imagedelivery.net/ELiLiOfYeVi79Put2yGLDg/${props.imageUid}/public`}
                    alt="Food image"
                />
            </div>

            <div className="mb-4">
                <p className="mb-1 text-sm text-gray-600">
                    Original: {props.originalName}
                </p>
            </div>

            {analysis == null ? (
                <>
                    <div className="mb-4">
                        <label className="mb-1 block text-sm font-medium">
                            Description (help the AI understand the image)
                        </label>
                        <textarea
                            className="w-full rounded-md border p-2"
                            rows={3}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="E.g., 'This is a chicken salad with ranch dressing, about 2 cups'"
                        />
                    </div>

                    <div className="flex gap-2">
                        <button
                            className="rounded-md bg-blue-500 px-4 py-2 text-white disabled:opacity-50"
                            onClick={handleAnalyze}
                            disabled={isLoading || description.trim() === ""}
                        >
                            {isLoading ? "Analyzing..." : "Analyze"}
                        </button>
                        <button
                            className="rounded-md bg-gray-300 px-4 py-2"
                            onClick={() => props.close(null)}
                        >
                            Cancel
                        </button>
                    </div>
                </>
            ) : (
                <>
                    <div className="mb-4">
                        <h3 className="mb-2 font-medium">Analysis: {analysis.name}</h3>
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b">
                                    <th className="py-1 text-left">Ingredient</th>
                                    <th className="py-1 text-right">Serving</th>
                                    <th className="py-1 text-right">Calories</th>
                                    <th className="py-1 text-right">Protein</th>
                                    <th className="py-1 text-right">Carbs</th>
                                    <th className="py-1 text-right">Fat</th>
                                </tr>
                            </thead>
                            <tbody>
                                {analysis.expanded_recipe.map((ing, i) => (
                                    <tr key={i} className="border-b">
                                        <td className="py-1">{ing.name}</td>
                                        <td className="py-1 text-right">
                                            {new Decimal(ing.quantity).toFixed(1)}{" "}
                                            {ing.unit}
                                        </td>
                                        <td className="py-1 text-right">
                                            {Number(ing.calories_pg ?? 0).toFixed(0)}
                                        </td>
                                        <td className="py-1 text-right">
                                            {Number(ing.protein_pg ?? 0).toFixed(1)}g
                                        </td>
                                        <td className="py-1 text-right">
                                            {Number(ing.carbs_pg ?? 0).toFixed(1)}g
                                        </td>
                                        <td className="py-1 text-right">
                                            {Number(ing.fat_pg ?? 0).toFixed(1)}g
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr className="font-semibold">
                                    <td className="py-1">Total</td>
                                    <td></td>
                                    <td className="py-1 text-right">
                                        {totalCalories?.toFixed(0)} cal
                                    </td>
                                    <td></td>
                                    <td></td>
                                    <td></td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>

                    <div className="flex gap-2">
                        <button
                            className="rounded-md bg-green-500 px-4 py-2 text-white disabled:opacity-50"
                            onClick={handleApprove}
                            disabled={isLoading}
                        >
                            {isLoading ? "Saving..." : "Approve & Replace"}
                        </button>
                        <button
                            className="rounded-md bg-gray-300 px-4 py-2"
                            onClick={() => setAnalysis(null)}
                        >
                            Try Again
                        </button>
                        <button
                            className="rounded-md bg-gray-300 px-4 py-2"
                            onClick={() => props.close(null)}
                        >
                            Cancel
                        </button>
                    </div>
                </>
            )}

            {error != null && <p className="mt-2 text-red-500">{error}</p>}
        </div>
    );
}

function LearningWeekModule(props: {
    module: Extract<
        server.backoffice.views.Backoffice["module"]["module"],
        {type: "learningweek"}
    >;
}) {
    const columns = [
        "id",
        "name",
        "quantity",
        "unit",
        "calories",
        "user_email",
        "created_at",
    ] as const;

    const {request} = useSiteContext();
    const url = new URL(request.url);
    const query = url.searchParams.get("query") ?? "";

    const analyzeDialog = useDialog<
        boolean,
        {
            entryId: number;
            entryUuid: string;
            promptUuid: string;
            imageUid: string;
            originalName: string;
        }
    >({
        Content: AnalyzeEntryDialogContent,
    });

    return (
        <>
            {analyzeDialog.dialog}
            <form action="" method="GET">
                <Form form={props.module.form} />
                <input type="text" name="query" defaultValue={query} />
                <button type="submit">Submit</button>
            </form>
            <h2>Learning Week Entries</h2>
            <table className="table-auto">
                <thead>
                    <tr className="text-left *:px-4">
                        <td></td>
                        {columns.map((column) => (
                            <th key={column}>{column}</th>
                        ))}
                        <th>Vision</th>
                    </tr>
                </thead>
                <tbody>
                    {props.module.results.map((result, index) => (
                        <tr key={index} className="*:px-4">
                            <td>
                                <input
                                    type="checkbox"
                                    name="is_checked"
                                    value={result.is_checked}
                                />
                            </td>
                            {columns.map((column) => (
                                <td key={column}>
                                    {column == "id" ? (
                                        <a href={result.admin_path}>{result.id}</a>
                                    ) : column == "created_at" ? (
                                        dates.formatBusinessDateTime(result[column])
                                    ) : column == "quantity" ? (
                                        new Decimal(result[column]).toFixed(1)
                                    ) : column == "calories" ? (
                                        result[column] != null ? (
                                            utils.formatDecimalAndUnit(
                                                result[column],
                                                "cal",
                                                false,
                                            )
                                        ) : (
                                            "–"
                                        )
                                    ) : (
                                        result[column]
                                    )}
                                </td>
                            ))}
                            <td>
                                {result.vision_prompt?.image?.image_uid != null && (
                                    <button
                                        type="button"
                                        onClick={() =>
                                            analyzeDialog({
                                                entryId: result.id,
                                                entryUuid: result.uuid,
                                                promptUuid: result.vision_prompt!.uuid,
                                                imageUid:
                                                    result.vision_prompt!.image!
                                                        .image_uid!,
                                                originalName: result.name,
                                            })
                                        }
                                    >
                                        <img
                                            className="max-h-[50px] cursor-pointer rounded-md object-contain hover:opacity-80"
                                            src={`https://imagedelivery.net/ELiLiOfYeVi79Put2yGLDg/${result.vision_prompt.image.image_uid}/public`}
                                            alt="Vision prompt"
                                        />
                                    </button>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </>
    );
}

export function Template(props: server.backoffice.views.Backoffice) {
    const {module} = props.module;

    if (module.type == "nutrition") {
        return (
            <Shell>
                <NutritionModule module={module} />
            </Shell>
        );
    }

    if (module.type == "rpc_request") {
        return (
            <Shell>
                <RPCRequestModule module={module} />
            </Shell>
        );
    }

    if (module.type == "dbtaskresult") {
        return (
            <Shell>
                <TaskModule module={module} />
            </Shell>
        );
    }

    if (module.type == "learningweek") {
        return (
            <Shell>
                <LearningWeekModule module={module} />
            </Shell>
        );
    }

    return (
        <Shell>
            <VisionModule module={module} />
        </Shell>
    );
}
